import { useState, type ReactElement } from 'react';
import type { CompetitionLabel, MarketSurveyRow } from '../../../domain/demand/marketViews';
import { formatInt } from '../../format';
import { DarkSectionHeader } from '../common/DarkSectionHeader';
import { PersonaDistributionBar, PersonaScale } from './PersonaDistributionBar';
import { personaMixOf } from './personaMix';
import './MarketPanels.css';

export interface MarketSurveyPanelProps {
  readonly rows: readonly MarketSurveyRow[];
  /** Called when an unserved route row is activated, to plan it in the market opportunity planner. */
  readonly onPlanRoute?: (originId: string, destinationId: string) => void;
}

const COMPETITION_TEXT: Readonly<Record<CompetitionLabel, string>> = {
  NONE: 'None',
  LIGHT: 'Light',
  MODERATE: 'Moderate',
  HEAVY: 'Heavy',
};

export function MarketSurveyPanel({ rows, onPlanRoute }: MarketSurveyPanelProps): ReactElement {
  const [mixKey, setMixKey] = useState<string | null>(null);
  const mixRow = rows.find((r) => r.routeKey === mixKey) ?? rows[0];
  return (
    <DarkSectionHeader
      title="Market survey"
      subtitle="Estimated weekly demand and competition by route"
      summary={`${formatInt(rows.length)} markets`}
      ariaLabel="Market survey"
    >
      {rows.length === 0 ? (
        <p className="mk-empty">No surveyed markets.</p>
      ) : (
        <div className="mk-table-wrap">
          <table className="mk-table">
            <thead>
              <tr>
                <th>Route</th>
                <th>Stage</th>
                <th>Weekly demand</th>
                <th>Offered seats</th>
                <th>Status</th>
                <th>Budget / premium</th>
                <th>Competition</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.routeKey}
                  className={row.status === 'UNSERVED' && onPlanRoute !== undefined ? 'mk-row-action' : undefined}
                  tabIndex={row.status === 'UNSERVED' && onPlanRoute !== undefined ? 0 : undefined}
                  title={row.status === 'UNSERVED' && onPlanRoute !== undefined ? 'Plan this route' : undefined}
                  onClick={row.status === 'UNSERVED' ? () => onPlanRoute?.(row.originId, row.destinationId) : undefined}
                  onKeyDown={
                    row.status === 'UNSERVED'
                      ? (event) => {
                          if (event.key === 'Enter') onPlanRoute?.(row.originId, row.destinationId);
                        }
                      : undefined
                  }
                >
                  <td>
                    <span className="fl-iata">{row.originId}</span> <span className="fl-iata">{row.destinationId}</span>
                  </td>
                  <td>{formatInt(row.stageDistanceKm)} km</td>
                  <td>
                    {formatInt(row.weeklyDemandLow)} – {formatInt(row.weeklyDemandHigh)}
                  </td>
                  <td>{formatInt(row.offeredSeats)}</td>
                  <td>
                    <span className={`fl-pill${row.status === 'ACTIVE' ? '' : ' fl-pill-warn'}`}>
                      {row.status === 'ACTIVE' ? 'Active' : 'Unserved'}
                    </span>
                  </td>
                  <td>
                    <span className="mk-mix" title={`${row.budgetPct.toFixed(0)}% budget, ${row.premiumPct.toFixed(0)}% premium`}>
                      <span className="mk-mix-premium" style={{ width: `${row.premiumPct}%` }} />
                    </span>{' '}
                    {row.budgetPct.toFixed(0)} / {row.premiumPct.toFixed(0)}%
                  </td>
                  <td>
                    {COMPETITION_TEXT[row.competition]}
                    {row.airlines.length > 0 ? ` (${row.airlines.join(', ')})` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {mixRow !== undefined && (
        <div className="mk-mix-panel">
          <div className="mk-mix-head">
            <h4 className="fl-section-title">Traveler mix</h4>
            <select aria-label="Market for traveler mix" value={mixRow.routeKey} onChange={(e) => setMixKey(e.target.value)}>
              {rows.map((r) => (
                <option key={r.routeKey} value={r.routeKey}>
                  {r.originId}–{r.destinationId}
                </option>
              ))}
            </select>
          </div>
          <PersonaScale />
          {personaMixOf(mixRow).map((p) => (
            <PersonaDistributionBar key={p.label} {...p} />
          ))}
          <p className="fl-sub">Indicative split of the survey's budget and premium shares; the demand model has no finer personas.</p>
        </div>
      )}
    </DarkSectionHeader>
  );
}
